import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import ProductCard from '@/components/ProductCard';

interface SimilarProductsProps {
  categoryId: string | null;
  subcategoryId?: string | null; // Added prop
  currentProductId: string;
}

const SimilarProducts = ({ categoryId, subcategoryId, currentProductId }: SimilarProductsProps) => {
  const [similarProducts, setSimilarProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSimilarProducts = async () => {
      if (!categoryId) {
        setLoading(false);
        return;
      }

      try {
        if (import.meta.env.DEV) {
          console.log('SimilarProducts: Fetching using RPC for', { categoryId, subcategoryId, currentProductId });
        }

        // Use the public catalog RPC which bypasses RLS restrictions
        const { data: allProducts, error: fetchError } = await supabase
          .rpc('get_public_product_catalog', {
            limit_count: 100,
            offset_count: 0
          });

        if (fetchError) {
          console.error('SimilarProducts: RPC Fetch Error:', fetchError);
          setLoading(false);
          return;
        }

        if (!allProducts || allProducts.length === 0) {
          if (import.meta.env.DEV) {
            console.log('SimilarProducts: Catalog is empty');
          }
          setSimilarProducts([]);
          setLoading(false);
          return;
        }

        // Filter for similar products in the same category, excluding current product
        let filtered = allProducts.filter((p: any) =>
          p.id !== currentProductId &&
          p.category_id === categoryId &&
          p.is_active !== false // Only active products
        );

        if (import.meta.env.DEV) {
          console.log(`SimilarProducts: Found ${filtered.length} products in same category`);
        }

        // If we have subcategory, prioritize products from that subcategory
        if (subcategoryId) {
          const inSameSub = filtered.filter((p: any) => p.subcategory_id === subcategoryId);
          const othersInCat = filtered.filter((p: any) => p.subcategory_id !== subcategoryId);
          // Combine: subcategory products first, then others in the same category
          filtered = [...inSameSub, ...othersInCat];
        }

        // Limit to 8 products
        const selected = filtered.slice(0, 8);

        if (selected.length > 0) {
          // Fetch category and subcategory names for enhancement
          const categoryIds = [...new Set(selected.map((p: any) => p.category_id).filter(Boolean))];
          const subcategoryIds = [...new Set(selected.map((p: any) => p.subcategory_id).filter(Boolean))];

          const [categoriesResult, subcategoriesResult] = await Promise.all([
            categoryIds.length > 0
              ? supabase.from('categories').select('id, name').in('id', categoryIds)
              : { data: [] },
            subcategoryIds.length > 0
              ? supabase.from('subcategories').select('id, name').in('id', subcategoryIds)
              : { data: [] }
          ]);

          const categoriesMap = new Map((categoriesResult.data || []).map(c => [c.id, c]));
          const subcategoriesMap = new Map((subcategoriesResult.data || []).map(s => [s.id, s]));

          const enhancedProducts = selected.map((product: any) => ({
            ...product,
            categories: product.category_id ? categoriesMap.get(product.category_id) || { name: 'ללא קטגוריה' } : { name: 'ללא קטגוריה' },
            subcategories: product.subcategory_id ? subcategoriesMap.get(product.subcategory_id) || { name: 'ללא תת-קטגוריה' } : { name: 'ללא תת-קטגוריה' }
          }));

          setSimilarProducts(enhancedProducts);
        } else {
          setSimilarProducts([]);
        }
      } catch (error) {
        console.error('SimilarProducts: Error fetching similar products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilarProducts();
  }, [categoryId, subcategoryId, currentProductId]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  if (similarProducts.length === 0) {
    return null;
  }

  return (
    <div className="my-8">
      <h2 className="text-2xl font-bold mb-6">מוצרים דומים</h2>
      <Carousel
        opts={{
          align: "start",
          direction: "rtl",
        }}
        className="w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {similarProducts.map((product) => (
            <CarouselItem key={product.id} className="pl-2 md:pl-4 basis-[80%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
              <ProductCard product={product} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="hidden md:flex" />
        <CarouselNext className="hidden md:flex" />
      </Carousel>
    </div>
  );
};

export default SimilarProducts;
